import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { assetUrl, serviceCategoryApi, serviceContentApi } from '../services/api';

type ServicesHeroContent = {
  badge_text: string;
  heading: string;
  description: string;
  button_text: string;
};

type ServiceCategoryCard = {
  id: number;
  name: string;
  description: string;
  image: string;
};

const DEFAULT_HERO: ServicesHeroContent = {
  badge_text: 'Services',
  heading: 'OUR EXPERTISE',
  description:
    "We don't just build structures; we create environments that inspire and engage. Discover our core specializations.",
  button_text: 'Explore Service',
};

export const SectionServices: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategoryCard[]>([]);
  const [heroContent, setHeroContent] = useState<ServicesHeroContent>(DEFAULT_HERO);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useRef(window.innerWidth >= 1024); // lg breakpoint

  // Load data (unchanged)
  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);
        const [categoryRes, contentRes] = await Promise.all([
          serviceCategoryApi.getAll(true),
          serviceContentApi.get().catch(() => null),
        ]);

        const categoryRows = Array.isArray(categoryRes?.data) ? categoryRes.data : [];
        setCategories(
          categoryRows.map((category: any, index: number) => ({
            id: Number(category.id || index + 1),
            name: String(category.name || ''),
            description: String(category.description || ''),
            image: assetUrl(category.image),
          }))
        );

        if (contentRes?.success && contentRes.data) {
          setHeroContent({
            badge_text: String(contentRes.data.badge_text || DEFAULT_HERO.badge_text),
            heading: String(contentRes.data.heading || DEFAULT_HERO.heading),
            description: String(contentRes.data.description || DEFAULT_HERO.description),
            button_text: String(contentRes.data.button_text || DEFAULT_HERO.button_text),
          });
        } else {
          setHeroContent(DEFAULT_HERO);
        }
      } catch {
        setCategories([]);
        setHeroContent(DEFAULT_HERO);
      } finally {
        setLoading(false);
      }
    };

    loadServices();
    const onUpdate = () => void loadServices();
    window.addEventListener('services:updated', onUpdate);
    return () => window.removeEventListener('services:updated', onUpdate);
  }, []);

  // Desktop: vertical wheel scroll -> horizontal scroll
  useEffect(() => {
    if (!isDesktop.current || categories.length === 0) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [categories]);

  // Update isDesktop on resize
  useEffect(() => {
    const handleResize = () => {
      isDesktop.current = window.innerWidth >= 1024;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll helper functions for arrows
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="relative overflow-hidden bg-[#050505] px-6 py-24 text-white md:px-8 md:py-32">
      <div className="absolute left-1/4 top-0 -z-10 h-96 w-96 rounded-full bg-amber-500/8 blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 -z-10 h-96 w-96 rounded-full bg-amber-400/8 blur-[120px]" />

      <div className="mx-auto max-w-7xl">
        <div className="mb-20 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <h2 className="text-5xl font-black tracking-tighter text-white md:text-7xl">
              {heroContent.heading.split(' ').map((word, index, words) => {
                const isLast = index === words.length - 1;
                return (
                  <React.Fragment key={`${word}-${index}`}>
                    <span className={isLast ? 'text-amber-500' : ''}>{word}</span>
                    {index < words.length - 1 ? ' ' : ''}
                  </React.Fragment>
                );
              })}
            </h2>
            <p className="mt-6 text-xl leading-relaxed text-zinc-400">{heroContent.description}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="hidden md:block"
          >
            <div className="mb-2 text-sm font-bold uppercase tracking-[0.3em] text-amber-500">
              {heroContent.badge_text}
            </div>
            <div className="h-1 w-24 bg-amber-500" />
          </motion.div>
        </div>

        {loading ? (
          <div className="rounded-[2rem] border border-white/10 bg-white/[0.03] p-10 text-center text-white/55">
            Loading categories...
          </div>
        ) : categories.length === 0 ? (
          <div className="rounded-[2rem] border border-white/10 bg-white/[0.03] p-10 text-center text-white/55">
            Categories abhi dashboard se add nahi hui hain.
          </div>
        ) : (
          <div className="relative">
            {/* Horizontal scroll container with hidden scrollbar */}
            <div
              ref={scrollContainerRef}
              className="flex flex-nowrap gap-6 overflow-x-auto pb-4"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <style>{`
                .flex::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {categories.map((category, index) => (
                <Link
                  key={`${category.id}-${category.name}`}
                  to={`/services?category=${encodeURIComponent(category.name)}`}
                  className="flex-shrink-0"
                >
                  <motion.article
                    initial={{ opacity: 0, y: 100 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-100px' }}
                    transition={{
                      delay: index * 0.1,
                      duration: 0.8,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    whileHover={{ y: -10 }}
                    className="group relative h-[500px] w-[280px] overflow-hidden rounded-2xl sm:w-[300px] md:w-[340px] lg:w-[360px]"
                  >
                    <div className="absolute inset-0">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="h-full w-full object-cover transition-all duration-1000 group-hover:scale-110"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-50" />
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-400/0 opacity-0 transition-opacity duration-500 group-hover:opacity-30" />
                    </div>

                    <div className="absolute inset-0 flex flex-col justify-end p-6">
                      <div className="relative z-10">
                        <h3 className="mb-3 text-3xl font-black tracking-tight text-white transition-colors group-hover:text-amber-500">
                          {category.name}
                        </h3>
                        <p className="text-base leading-relaxed text-zinc-300 transition-all duration-500 line-clamp-3">
                          {category.description}
                        </p>
                      </div>
                    </div>

                    <div className="pointer-events-none absolute inset-4 rounded-2xl border border-white/0 transition-all duration-700 group-hover:border-white/10" />
                  </motion.article>
                </Link>
              ))}
            </div>

            {/* Desktop navigation arrows (visible only on lg screens) */}
            <div className="absolute -left-4 top-1/2 hidden -translate-y-1/2 lg:block">
              <button
                onClick={scrollLeft}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition-all hover:bg-amber-500 hover:text-black"
                aria-label="Scroll left"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
            </div>
            <div className="absolute -right-4 top-1/2 hidden -translate-y-1/2 lg:block">
              <button
                onClick={scrollRight}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition-all hover:bg-amber-500 hover:text-black"
                aria-label="Scroll right"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-6 w-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};